import React, { useState } from 'react';
import { Box, ButtonBase, Checkbox, InputBase, Link, Popover, Typography } from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { GREEN, GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';

// Stile comune dei filtri "a pillola": una riga, icona + valore; verde quando il filtro è attivo
export const PILL_HEIGHT = 36;
export const pillSx = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: PILL_HEIGHT,
  boxSizing: 'border-box',
  borderRadius: 2,
  border: '1px solid',
  borderColor: active ? GREEN : 'var(--rt-pill-border)',
  bgcolor: active ? GREEN_SOFT : 'var(--rt-paper)',
  color: active ? GREEN_DARK : INK,
  transition: 'border-color 0.15s ease',
  '&:hover': { borderColor: active ? GREEN_DARK : 'var(--rt-pill-border-hover)' },
});
// Gruppo di scelte esclusive (ToggleButtonGroup) con la stessa forma delle pillole
export const segmentedSx = {
  '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, height: PILL_HEIGHT, fontSize: 13.5, fontWeight: 500, color: INK, borderColor: 'var(--rt-pill-border)' },
  '& .MuiToggleButton-root:first-of-type': { borderRadius: '8px 0 0 8px' },
  '& .MuiToggleButton-root:last-of-type': { borderRadius: '0 8px 8px 0' },
  '& .Mui-selected': { bgcolor: `${GREEN_SOFT} !important`, color: `${GREEN_DARK} !important`, fontWeight: 700 },
};
// Pulsanti secondari della barra filtri (Reset, Export) alla stessa altezza
export const barButtonSx = { textTransform: 'none', height: PILL_HEIGHT, borderRadius: 2, borderColor: 'var(--rt-pill-border)', color: INK, fontWeight: 600 };
const pillTextSx = (active) =>({ fontSize: 13.5, fontWeight: active ? 700 : 500, color: 'inherit', lineHeight: 1 });
const iconSx = (active) => ({ display: 'flex', '& svg': { fontSize: 18, color: active ? GREEN_DARK : MUTED } });

/**
 * Filtro a selezione multipla per id: pillola con il valore attuale e pannello con ricerca e caselle.
 * Nessuna selezione = tutti. options: [{ id, name, caption? }]
 * label è il nome al plurale, usato nel conteggio ("3 projects") e nella ricerca.
 */
export default function MultiSelectFilter({ label, allLabel, options, value, onChange, disabled, width = 260, icon }) {
  const [anchor, setAnchor] = useState(null);
  const [query, setQuery] = useState('');
  const selected = options.filter((o) => value.includes(o.id));
  const active = selected.length > 0;
  const text = !active ? allLabel : selected.length === 1 ? selected[0].name : `${selected.length} ${label.toLowerCase()}`;
  const q = query.trim().toLowerCase();
  const visible = q ? options.filter((o) => o.name.toLowerCase().includes(q) || (o.caption || '').toLowerCase().includes(q)) : options;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const close = () => {
    setAnchor(null);
    setQuery('');
  };

  return (
    <>
      <Box sx={{ ...pillSx(active), maxWidth: width, opacity: disabled ? 0.55 : 1 }}>
        <ButtonBase
          disabled={disabled}
          onClick={(e) => setAnchor(e.currentTarget.parentElement)}
          aria-label={`${label}: ${text}`}
          title={active ? selected.map((o) => o.name).join(', ') : undefined}
          sx={{ height: '100%', gap: 0.75, pl: 1.25, pr: active ? 0.5 : 0.75, minWidth: 0, borderRadius: 2, justifyContent: 'flex-start' }}
        >
          <Box sx={iconSx(active)}>{icon || <FilterIcon />}</Box>
          <Typography noWrap sx={pillTextSx(active)}>
            {text}
          </Typography>
          {!active && <ArrowDropDownIcon sx={{ fontSize: 20, color: MUTED, ml: -0.25 }} />}
        </ButtonBase>
        {active && (
          <ButtonBase onClick={() => onChange([])} aria-label={`Clear ${label.toLowerCase()} filter`} sx={{ mr: 0.75, borderRadius: '50%', p: 0.25, '&:hover': { bgcolor: 'rgba(59,127,44,0.12)' } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </ButtonBase>
        )}
      </Box>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 0.75, width: 320, borderRadius: 2, border: `1px solid ${LINE}`, boxShadow: '0 8px 24px rgba(0,0,0,0.10)' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${LINE}` }}>
          <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
          <InputBase autoFocus fullWidth placeholder={`Search ${label.toLowerCase()}`} value={query} onChange={(e) => setQuery(e.target.value)} sx={{ fontSize: 13.5 }} />
        </Box>
        <Box sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
          {visible.length === 0 ? (
            <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: MUTED }}>No matches</Typography>
          ) : (
            visible.map((o) => (
              <ButtonBase
                key={o.id}
                onClick={() => toggle(o.id)}
                sx={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 0.75, px: 1, py: 0.25, '&:hover': { bgcolor: 'var(--rt-hover)' } }}
              >
                <Checkbox size="small" color="success" checked={value.includes(o.id)} tabIndex={-1} sx={{ p: 0.5 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, color: INK }}>
                    {o.name}
                  </Typography>
                  {o.caption && (
                    <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                      {o.caption}
                    </Typography>
                  )}
                </Box>
              </ButtonBase>
            ))
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderTop: `1px solid ${LINE}` }}>
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>{active ? `${selected.length} selected` : allLabel}</Typography>
          {active && (
            <Link component="button" type="button" underline="hover" onClick={() => onChange([])} sx={{ fontSize: 12.5, fontWeight: 600, color: GREEN_DARK }}>
              Clear
            </Link>
          )}
        </Box>
      </Popover>
    </>
  );
}

/** Ricerca testuale nello stesso stile dei filtri a pillola, con × per svuotarla. */
export function SearchFilter({ value, onChange, placeholder, width = 280 }) {
  const active = !!value;
  return (
    <Box sx={{ ...pillSx(active), width, px: 1.25, gap: 0.75, '&:focus-within': { borderColor: GREEN } }}>
      <Box sx={iconSx(active)}>
        <SearchIcon />
      </Box>
      <InputBase
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
        sx={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: active ? 700 : 500, color: 'inherit', '& input::placeholder': { color: MUTED, opacity: 1, fontWeight: 500 } }}
      />
      {active && (
        <ButtonBase onClick={() => onChange('')} aria-label="Clear search" sx={{ borderRadius: '50%', p: 0.25, '&:hover': { bgcolor: 'rgba(59,127,44,0.12)' } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </ButtonBase>
      )}
    </Box>
  );
}

/** Interruttore on/off nello stesso stile (al posto delle caselle di spunta nelle barre filtri). */
export function TogglePill({ label, checked, onChange, icon }) {
  return (
    <ButtonBase onClick={() => onChange(!checked)} aria-pressed={checked} sx={{ ...pillSx(checked), gap: 0.75, px: 1.25 }}>
      <Box sx={iconSx(checked)}>{checked ? <CheckIcon /> : icon || <CheckIcon sx={{ opacity: 0.35 }} />}</Box>
      <Typography noWrap sx={pillTextSx(checked)}>
        {label}
      </Typography>
    </ButtonBase>
  );
}
